import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useSearchParams } from 'react-router';
import {
  ApiError,
  api,
  historyApi,
  type HistoryArchiveState,
  type HistoryFileTable,
  type HistoryMessage,
  type HistoryMessagesView,
  type HistoryPatch,
  type HistoryPatchesView,
  type HistoryVoicemail,
  type HistoryVoicemailView
} from '../../api/client';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Drawer, Empty, Field, LoadError, Loading, Notice, PageHeader, SelectField } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { fullWhen, plural, shownNumber, sizeOf } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { timesIn } from '../../lib/time';
import { useData } from '../../lib/useData';
import { WHEN, duration, fullDay, lineOptions, rangeOf, type When } from './Calls';

/**
 * History's other tabs (Storage A1 part 2, 1.13.0, Teampace 2716; help: call-history): Voicemail,
 * Messages and Patches, each one search over every date the archive holds, and the archive's
 * recordings, voicemail and files played or downloaded through Stitch (each listen and download is
 * logged). Like Call history, the filters live in the address.
 */

const PAGE_SIZE = 50;

/** Calls · Voicemail · Messages · Patches · Meetings, at the top of each. */
export function HistoryTabs() {
  const { tenant } = useTenant();
  const base = `/t/${encodeURIComponent(tenant.domain)}/calls`;
  const tabs = [
    { to: base, label: 'Calls', end: true },
    { to: `${base}/voicemail`, label: 'Voicemail' },
    { to: `${base}/messages`, label: 'Messages' },
    { to: `${base}/patches`, label: 'Patches' },
    // Storage A3 (3086): meetings and webinars, with attendance (MeetingHistory.tsx).
    { to: `${base}/meetings`, label: 'Meetings' }
  ];
  return (
    <nav className="filters history-tabs" aria-label="History">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end}>
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** The When filter and the search box, read from and written to the address. */
export function useHistoryFilters() {
  const [params, setParams] = useSearchParams();
  const latest = useRef(params);
  useEffect(() => {
    latest.current = params;
  }, [params]);
  const when = ((): When => {
    const w = params.get('when');
    return WHEN.some((x) => x.value === w) ? (w as When) : '30';
  })();
  const { from, to } = rangeOf(when, { from: params.get('from') ?? undefined, to: params.get('to') ?? undefined });
  const q = (params.get('q') ?? '').trim().slice(0, 80);
  const set = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(latest.current);
    for (const [k, v] of Object.entries(changes)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    latest.current = next;
    setParams(next, { replace: true });
  };
  const clear = () => {
    latest.current = new URLSearchParams();
    setParams(new URLSearchParams(), { replace: true });
  };
  // The search box: the list follows a moment after typing stops.
  const [text, setText] = useState(q);
  useEffect(() => setText(q), [q]);
  useEffect(() => {
    if (text.trim() === q) return;
    const t = setTimeout(() => set({ q: text.trim() || undefined }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return { params, when, from, to, q, set, clear, text, setText };
}

/** One list from the archive: its first page whenever the search changes, then Load more. */
export function useArchiveList<V extends { next: string | null }, T>(load: (cursor: string | undefined) => Promise<V>, pick: (v: V) => T[], key: string) {
  const [items, setItems] = useState<T[]>([]);
  const [last, setLast] = useState<V | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [tick, setTick] = useState(0);
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    load(undefined)
      .then((r) => {
        if (mine !== seq.current) return;
        setItems(pick(r));
        setLast(r);
        setError(undefined);
      })
      .catch((e: unknown) => mine === seq.current && setError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => mine === seq.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);
  const more = async () => {
    if (!last?.next) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const r = await load(last.next);
      if (mine !== seq.current) return;
      setItems((cur) => [...cur, ...pick(r)]);
      setLast(r);
    } catch (e) {
      if (mine === seq.current) setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  };
  return { items, last, loading, error, more, retry: () => setTick((t) => t + 1) };
}

/** When, the dates for "Choose dates", and the search box's place in the header. */
export function WhenFields({ f }: { f: ReturnType<typeof useHistoryFilters> }) {
  return (
    <>
      <SelectField label="When" value={f.when} onChange={(e) => f.set({ when: e.target.value === '30' ? undefined : e.target.value, ...(e.target.value === 'custom' ? { from: f.from, to: f.to } : { from: undefined, to: undefined }) })} options={WHEN} />
      {f.when === 'custom' && (
        <>
          <Field label="From" type="date" value={f.from} max={f.to} onChange={(e) => f.set({ from: e.target.value || undefined })} />
          <Field label="To" type="date" value={f.to} min={f.from} onChange={(e) => f.set({ to: e.target.value || undefined })} />
        </>
      )}
    </>
  );
}

export function SearchBox({ f, label, placeholder }: { f: ReturnType<typeof useHistoryFilters>; label: string; placeholder: string }) {
  return (
    <label className="search">
      <Icon name="search" size={17} strokeWidth={2} />
      <span className="sr-only">{label}</span>
      <input type="search" placeholder={placeholder} value={f.text} onChange={(e) => f.setText(e.target.value)} />
    </label>
  );
}

/** How the archive answered, and from when it has this kind of item. */
export function ArchiveNotices({ state, keptSince, from, done, what }: { state: HistoryArchiveState | undefined; keptSince: string | null | undefined; from: string; done: boolean; what: string }) {
  return (
    <>
      {state === 'not-set-up' && <Notice>{`Stitch’s archive, where History finds your ${what}, isn’t set up for your company yet.`}</Notice>}
      {state === 'unavailable' && <Notice tone="warn">Stitch’s archive isn’t answering right now. Try again in a few minutes.</Notice>}
      {state === 'your-storage-unreachable' && <Notice tone="warn">Some of these are only in your own storage, which isn’t answering. Settings → Storage and retention shows what it said.</Notice>}
      {state === 'ok' && done && keptSince && from < keptSince && <Notice>{`The archive has your ${what} since ${fullDay(keptSince)}.`}</Notice>}
    </>
  );
}

export function ListFoot({ show, loading, onMore }: { show: boolean; loading: boolean; onMore: () => void }) {
  if (!show) return null;
  return (
    <div className="card-foot">
      <Button variant="ghost" disabled={loading} onClick={onMore}>
        {loading ? 'Loading…' : 'Load more'}
      </Button>
    </div>
  );
}

const who = (p: { name: string; number: string }) => (p.name && p.number ? `${p.name} (${shownNumber(p.number)})` : p.name || shownNumber(p.number));
export const kept = (from_: string) =>
  from_ === 'your-storage' ? (
    <Chip small tone="brand">
      From your storage
    </Chip>
  ) : null;
export const lengthBetween = (a: string, b: string | null) => (b ? Math.max(0, Math.round((Date.parse(b) - Date.parse(a)) / 1000)) : 0);

// ---- Voicemail ----------------------------------------------------------------------------

export function VoicemailHistory() {
  const { tenant } = useTenant();
  const f = useHistoryFilters();
  const mailbox = (f.params.get('mailbox') ?? '').replace(/[^0-9*#]/g, '').slice(0, 12);
  const dir = useData(() => api.directory(tenant.domain).catch(() => undefined), [tenant.domain]);
  const search = { from: f.from, to: f.to, ...(f.q ? { q: f.q } : {}), ...(mailbox ? { mailbox } : {}) };
  const list = useArchiveList<HistoryVoicemailView, HistoryVoicemail>((cursor) => historyApi.voicemail(tenant.domain, { ...search, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }), (v) => v.voicemails, `${tenant.domain}${JSON.stringify(search)}`);
  const [open, setOpen] = useState<HistoryVoicemail | null>(null);
  const filtered = Boolean(f.q || mailbox || f.when !== '30');
  return (
    <>
      <PageHeader eyebrow={`${list.items.length ? `${plural(list.items.length, 'voicemail')}${list.last?.next ? '+' : ''} · ` : ''}${timesIn()}`} title="Voicemail history" actions={<SearchBox f={f} label="Search voicemail" placeholder="Search name or number" />} />
      <div className="content">
        <HistoryTabs />
        <div className="call-filters" role="group" aria-label="Filter voicemail">
          <WhenFields f={f} />
          <SelectField label="Mailbox" value={mailbox} onChange={(e) => f.set({ mailbox: e.target.value })} options={lineOptions(dir.data, mailbox).map((o) => (o.value === '' ? { ...o, label: 'Every mailbox' } : o))} />
          {filtered && (
            <div className="call-filters-clear">
              <Button variant="ghost" onClick={f.clear}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
        {list.last?.scope === 'own' && <Notice>Showing the voicemail left in your own mailbox (and your role’s call queues’).</Notice>}
        <ArchiveNotices state={list.last?.archive} keptSince={list.last?.keptSince} from={f.from} done={!list.loading && !list.last?.next} what="voicemail" />
        <Card>
          {list.error && list.items.length === 0 ? (
            <LoadError error={list.error} retry={list.retry} />
          ) : list.loading && list.items.length === 0 ? (
            <Loading label="Loading voicemail" />
          ) : list.items.length === 0 ? (
            <Empty title={filtered ? 'No voicemail matches' : 'No voicemail in the archive yet'}>
              {filtered ? 'Try other dates, or fewer filters.' : 'Each new voicemail is archived within a few minutes of being left.'}
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="table calls">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">From</th>
                    <th scope="col">Mailbox</th>
                    <th scope="col">Length</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((v) => (
                    <tr key={`${v.from_}-${v.id}`}>
                      <td>
                        <span className="call-when">
                          <button type="button" className="link-btn" onClick={() => setOpen(v)} aria-label={`Open the voicemail at ${fullWhen(v.leftAt)}`}>
                            {fullWhen(v.leftAt)}
                          </button>
                          {kept(v.from_)}
                        </span>
                        {v.transcript && <div className="muted small history-snippet">{v.transcript}</div>}
                      </td>
                      <td>{who(v.from) || <span className="muted">Unknown</span>}</td>
                      <td>{v.mailboxName ? `${v.mailboxName} (${v.mailbox})` : v.mailbox}</td>
                      <td className="mono">{duration(v.seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ListFoot show={list.items.length > 0 && Boolean(list.last?.next)} loading={list.loading} onMore={() => void list.more()} />
        </Card>
      </div>
      <Drawer open={open !== null} onClose={() => setOpen(null)} title={open ? `Voicemail from ${who(open.from) || 'Unknown'}` : 'Voicemail'} subtitle={open ? fullWhen(open.leftAt) : ''}>
        {open && (
          <div className="stack">
            <dl className="dl">
              <dt>Mailbox</dt>
              <dd>{open.mailboxName ? `${open.mailboxName} (${open.mailbox})` : open.mailbox}</dd>
              <dt>Length</dt>
              <dd>{duration(open.seconds)}</dd>
            </dl>
            <ArchivedFiles domain={tenant.domain} table="voicemails" id={open.id} at={open.leftAt} />
          </div>
        )}
      </Drawer>
    </>
  );
}

// ---- Messages -----------------------------------------------------------------------------

const KINDS = [
  { value: '', label: 'Every conversation' },
  { value: 'channel', label: 'Channels' },
  { value: 'dm', label: 'Direct messages' },
  { value: 'text', label: 'Texts' }
];

function conversationLabel(m: HistoryMessage): string {
  if (m.conversation.kind === 'text') return `Text with ${m.text?.otherNumber ? shownNumber(m.text.otherNumber) : 'someone'}`;
  if (m.conversation.kind === 'channel') return `#${m.conversation.name || 'channel'}`;
  return m.conversation.name || 'Direct message';
}

/** Whether the person viewing is one of the company's own people (Stitch staff aren't: messages are the company's). */
export function useMember(domain: string): boolean {
  const { state } = useAuth();
  return Boolean(state?.user && state.memberships?.some((m) => m.tenant === domain.toLowerCase()));
}

export function MessageHistory() {
  const { tenant } = useTenant();
  // Stitch staff looking at a customer's portal: messages are the company's own, so nothing is asked for.
  if (!useMember(tenant.domain))
    return (
      <>
        <PageHeader eyebrow={timesIn()} title="Message history" />
        <div className="content">
          <HistoryTabs />
          <Notice icon="lock" title="Messages are private to the company">
            {tenant.name}’s channels, direct messages and texts are for its own people. Stitch staff don’t see them.
          </Notice>
        </div>
      </>
    );
  return <MessageHistoryList />;
}

function MessageHistoryList() {
  const { tenant } = useTenant();
  const f = useHistoryFilters();
  const kind = (['channel', 'dm', 'text'] as const).find((k) => k === f.params.get('kind'));
  const person = (f.params.get('person') ?? '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
  const filesOnly = f.params.get('files') === 'yes';
  const peopleList = useData(() => api.messagePeople(tenant.domain).catch(() => ({ people: [] })), [tenant.domain], { live: false });
  const search = { from: f.from, to: f.to, ...(f.q ? { q: f.q } : {}), ...(kind ? { kind } : {}), ...(person ? { person } : {}), ...(filesOnly ? { files: 'yes' as const } : {}) };
  const list = useArchiveList<HistoryMessagesView, HistoryMessage>((cursor) => historyApi.messages(tenant.domain, { ...search, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }), (v) => v.messages, `${tenant.domain}${JSON.stringify(search)}`);
  const filtered = Boolean(f.q || kind || person || filesOnly || f.when !== '30');
  const notAllowed = list.error instanceof ApiError && list.error.status === 403;
  const base = `/t/${encodeURIComponent(tenant.domain)}`;
  return (
    <>
      <PageHeader eyebrow={`${list.items.length ? `${plural(list.items.length, 'message')}${list.last?.next ? '+' : ''} · ` : ''}${timesIn()}`} title="Message history" actions={<SearchBox f={f} label="Search messages" placeholder="Search words, names or a number" />} />
      <div className="content">
        <HistoryTabs />
        <div className="call-filters" role="group" aria-label="Filter messages">
          <WhenFields f={f} />
          <SelectField label="Conversations" value={kind ?? ''} onChange={(e) => f.set({ kind: e.target.value })} options={KINDS} />
          <SelectField
            label="From"
            value={person}
            onChange={(e) => f.set({ person: e.target.value })}
            options={[{ value: '', label: 'Anyone' }, ...(peopleList.data?.people ?? []).map((p) => ({ value: p.id, label: p.name })), ...(person && !(peopleList.data?.people ?? []).some((p) => p.id === person) ? [{ value: person, label: 'Someone who left' }] : [])]}
          />
          <label className="check-row history-check">
            <input type="checkbox" checked={filesOnly} onChange={(e) => f.set({ files: e.target.checked ? 'yes' : undefined })} />
            <span>With files only</span>
          </label>
          {filtered && (
            <div className="call-filters-clear">
              <Button variant="ghost" onClick={f.clear}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
        {list.last?.scope === 'all' && <Notice>Showing every archived message: your company lets its admins search them all (Settings → Storage and retention). Everyone sees that this is on in Messages.</Notice>}
        {list.last?.scope === 'member' && (
          <Notice>
            Showing the conversations you can read in Messages.
            {list.last.admin && !list.last.adminsSearch && (
              <>
                {' '}
                Admins can search every archived message once your company turns that on in <Link to={`${base}/settings/storage`}>Storage and retention</Link>.
              </>
            )}
          </Notice>
        )}
        <ArchiveNotices state={list.last?.archive} keptSince={list.last?.keptSince} from={f.from} done={!list.loading && !list.last?.next} what="messages" />
        <Card>
          {notAllowed ? (
            <div className="card-body">
              <Notice title="Not available">{list.error!.message}</Notice>
            </div>
          ) : list.error && list.items.length === 0 ? (
            <LoadError error={list.error} retry={list.retry} />
          ) : list.loading && list.items.length === 0 ? (
            <Loading label="Loading messages" />
          ) : list.items.length === 0 ? (
            <Empty title={filtered ? 'No messages match' : 'No messages in the archive yet'}>{filtered ? 'Try other dates, or fewer filters.' : 'Messages are archived within a few minutes of being sent.'}</Empty>
          ) : (
            <ul className="history-messages">
              {list.items.map((m) => (
                <li key={`${m.from_}-${m.id}`}>
                  <div className="history-message-head">
                    <strong>{conversationLabel(m)}</strong>
                    <span className="muted small">
                      {m.author.name || (m.text?.direction === 'in' ? 'Them' : 'Stitch')} · {fullWhen(m.sentAt)}
                      {m.editedAt ? ' · edited' : ''}
                    </span>
                    {kept(m.from_)}
                  </div>
                  {m.body && <p className="history-message-body">{m.body}</p>}
                  {m.files.length > 0 && (
                    <div className="history-files">
                      {m.files.map((x) => (
                        <a key={x.id} className="chip-link" href={historyApi.fileUrl(tenant.domain, 'messages', m.id, x.n, m.sentAt, true)} download>
                          <Icon name="download" size={14} /> {x.name ?? `File ${x.n + 1}`}
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <ListFoot show={list.items.length > 0 && Boolean(list.last?.next)} loading={list.loading} onMore={() => void list.more()} />
        </Card>
      </div>
    </>
  );
}

// ---- Patches ------------------------------------------------------------------------------

export function PatchHistory() {
  const { tenant } = useTenant();
  const f = useHistoryFilters();
  const person = (f.params.get('person') ?? '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
  // The company's people, from Messages: only its own people may ask (Stitch staff see names in the list).
  const member = useMember(tenant.domain);
  const peopleList = useData(() => (member ? api.messagePeople(tenant.domain).catch(() => ({ people: [] })) : Promise.resolve({ people: [] })), [tenant.domain, member], { live: false });
  const search = { from: f.from, to: f.to, ...(f.q ? { q: f.q } : {}), ...(person ? { person } : {}) };
  const list = useArchiveList<HistoryPatchesView, HistoryPatch>((cursor) => historyApi.patches(tenant.domain, { ...search, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }), (v) => v.patches, `${tenant.domain}${JSON.stringify(search)}`);
  const filtered = Boolean(f.q || person || f.when !== '30');
  return (
    <>
      <PageHeader eyebrow={`${list.items.length ? `${plural(list.items.length, 'Patch', 'Patches')}${list.last?.next ? '+' : ''} · ` : ''}${timesIn()}`} title="Patch history" actions={<SearchBox f={f} label="Search Patches" placeholder="Search a name" />} />
      <div className="content">
        <HistoryTabs />
        <div className="call-filters" role="group" aria-label="Filter Patches">
          <WhenFields f={f} />
          <SelectField label="With" value={person} onChange={(e) => f.set({ person: e.target.value })} options={[{ value: '', label: 'Anyone' }, ...(peopleList.data?.people ?? []).map((p) => ({ value: p.id, label: p.name }))]} />
          {filtered && (
            <div className="call-filters-clear">
              <Button variant="ghost" onClick={f.clear}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
        {list.last?.scope === 'own' && <Notice>Showing the Patches you were in.</Notice>}
        <ArchiveNotices state={list.last?.archive} keptSince={list.last?.keptSince} from={f.from} done={!list.loading && !list.last?.next} what="Patches" />
        <Card>
          {list.error && list.items.length === 0 ? (
            <LoadError error={list.error} retry={list.retry} />
          ) : list.loading && list.items.length === 0 ? (
            <Loading label="Loading Patches" />
          ) : list.items.length === 0 ? (
            <Empty title={filtered ? 'No Patches match' : 'No Patches in the archive yet'}>{filtered ? 'Try other dates, or fewer filters.' : 'A Patch is archived once it ends.'}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table calls">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Started by</th>
                    <th scope="col">Who was in it</th>
                    <th scope="col">Length</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((x) => (
                    <tr key={`${x.from_}-${x.id}`}>
                      <td>
                        <span className="call-when">
                          {fullWhen(x.startedAt)}
                          {kept(x.from_)}
                        </span>
                        {x.conversation?.name && <div className="muted small">#{x.conversation.name}</div>}
                      </td>
                      <td>{x.startedBy.name || <span className="muted">Unknown</span>}</td>
                      <td>{x.people.map((p) => p.name).join(', ') || <span className="muted">–</span>}</td>
                      <td className="mono">{duration(lengthBetween(x.startedAt, x.endedAt))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ListFoot show={list.items.length > 0 && Boolean(list.last?.next)} loading={list.loading} onMore={() => void list.more()} />
        </Card>
      </div>
    </>
  );
}

// ---- Playback and downloads ---------------------------------------------------------------

/**
 * What an archived item has, played or downloaded through Stitch: a call's recordings, a voicemail,
 * a message's files; and its transcript when there is one.
 */
export function ArchivedFiles({ domain, table, id, at }: { domain: string; table: HistoryFileTable; id: string; at: string }) {
  const data = useData(() => historyApi.files(domain, table, id, at), [domain, table, id, at], { live: false });
  const body = useMemo((): ReactNode => {
    if (data.error) return <Notice tone="warn">{data.error instanceof ApiError ? data.error.message : 'The archive couldn’t send this just now.'}</Notice>;
    if (!data.data) return <Loading label="Finding it in the archive" />;
    if (!data.data.files.length) return <p className="muted small">{table === 'calls' ? 'Its recording isn’t in the archive yet.' : 'Nothing to play here yet.'}</p>;
    return data.data.files.map((x) => {
      const src = historyApi.fileUrl(domain, table, id, x.n, at);
      const download = historyApi.fileUrl(domain, table, id, x.n, at, true);
      return (
        <div key={x.n} className="stack history-file">
          {x.type.startsWith('audio/') && <audio controls preload="none" src={src} aria-label={table === 'voicemails' ? 'Voicemail' : 'Recording'} />}
          {x.type.startsWith('image/') && <img src={src} alt={x.name} className="history-image" />}
          <a className="chip-link" href={download} download>
            <Icon name="download" size={14} /> Download {x.name}
            {x.bytes ? ` (${sizeOf(x.bytes)})` : ''}
          </a>
        </div>
      );
    });
  }, [data.data, data.error, domain, table, id, at]);
  return (
    <div className="stack">
      <h3 className="section-title">{table === 'messages' ? 'Files' : table === 'voicemails' ? 'Listen' : 'Recording'}</h3>
      {body}
      {data.data?.transcript && (
        <>
          <h3 className="section-title">Transcript</h3>
          <p className="history-transcript">{data.data.transcript}</p>
        </>
      )}
      <p className="muted small">Played and downloaded through Stitch, from its archive. Each listen and download is logged.</p>
    </div>
  );
}
